import React from 'react'
import type { CustomRenewalPreview } from '@homevest/types/renewals'
import RenewalBillingReview from './RenewalBillingReview'
import { useParams } from 'react-router'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast
} from '@chakra-ui/react'

import axios from 'lib/axios'
import { formatDate } from 'lib/date-time'
import { RenewalDetails } from 'types/Renewals'
import RenewRentalConfirmationDetails from './RenewRentalConfirmationDetails'

const RenewRentalConfirmation = ({
  renewalDetails,
  billingPreview,
  isOpen,
  setOpen,
  goBack,
  onSuccess
}: {
  renewalDetails: RenewalDetails
  billingPreview?: CustomRenewalPreview
  isOpen: boolean
  setOpen: (closed: boolean) => void
  goBack: (closed: boolean) => void
  onSuccess?: () => void
}) => {
  const { rentalId } = useParams<{ rentalId: string }>()
  const [isSending, setIsSending] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [acknowledged, setAcknowledged] = React.useState(false)
  const submissionInFlightRef = React.useRef(false)
  const modalBodyRef = React.useRef<HTMLDivElement>(null)
  const toast = useToast()

  React.useEffect(() => {
    if (isOpen) {
      modalBodyRef.current?.scrollTo({ top: 0 })
      setErrorMessage('')
      setAcknowledged(false)
    }
  }, [isOpen])

  const handleClose = () => {
    if (!submissionInFlightRef.current) setOpen(false)
  }

  const handleSubmit = async () => {
    if (submissionInFlightRef.current || (billingPreview?.postedCharges.length && !acknowledged))
      return

    submissionInFlightRef.current = true
    setIsSending(true)
    setErrorMessage('')

    try {
      const {
        agreementTemplateName: _agreementTemplateName,
        signers: _signers,
        ...issuanceDetails
      } = renewalDetails
      const payload = {
        ...(renewalDetails.leaseVariant === 'kcpoa'
          ? {
              leaseVariant: renewalDetails.leaseVariant,
              extensionMonthsInDigits: renewalDetails.extensionMonthsInDigits,
              useAlignedRenewalDates: renewalDetails.useAlignedRenewalDates,
              newLeaseRent: renewalDetails.newLeaseRent,
              notes: renewalDetails.notes,
              kcpoaTerms: renewalDetails.kcpoaTerms
            }
          : issuanceDetails),
        ...(billingPreview
          ? {
              previewVersion: billingPreview.previewVersion,
              acknowledgePostedCharges: acknowledged
            }
          : {}),
        newLeaseStartDate: formatDate(renewalDetails.newLeaseStartDate),
        newLeaseEndDate: formatDate(renewalDetails.newLeaseEndDate)
      }

      await axios.post(`/admin/renew_rental/${rentalId}`, payload)
      setOpen(false)
      onSuccess?.()
      toast({
        title: 'Renewal sent',
        description: 'The renewal agreement was created and sent for signature.',
        status: 'success',
        duration: 6000,
        isClosable: true,
        position: 'top-right'
      })
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message ??
          error?.response?.data?.error ??
          error?.message ??
          'Unable to issue the renewal.'
      )
    } finally {
      submissionInFlightRef.current = false
      setIsSending(false)
    }
  }

  return (
    <Modal
      closeOnEsc={!isSending}
      closeOnOverlayClick={!isSending}
      isOpen={isOpen}
      onClose={handleClose}
      scrollBehavior='inside'
      size='4xl'
    >
      <ModalOverlay bg='blackAlpha.500' />
      <ModalContent maxH='calc(100vh - 2rem)' mx={4} my={4}>
        <ModalHeader pb={1}>
          {billingPreview ? '3. Review and send' : 'Review custom renewal'}
        </ModalHeader>
        <ModalCloseButton isDisabled={isSending} />
        <ModalBody ref={modalBodyRef} pb={0}>
          <Stack spacing={5}>
            <Text color='gray.600'>Confirm the terms before creating the signature request.</Text>
            <RenewRentalConfirmationDetails
              renewalDetails={renewalDetails}
              billingPreview={billingPreview}
            />
            {billingPreview && (
              <RenewalBillingReview
                preview={billingPreview}
                requestedOptionPremium={
                  renewalDetails.leaseVariant === 'standard'
                    ? renewalDetails.optionPremium
                    : undefined
                }
                acknowledged={acknowledged}
                setAcknowledged={setAcknowledged}
              />
            )}
          </Stack>
        </ModalBody>
        <ModalFooter pt={5}>
          <Stack spacing={4} w='full'>
            {errorMessage && (
              <Alert status='error' borderRadius='md'>
                <AlertIcon />
                <Box>
                  <AlertTitle>Unable to send renewal</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Box>
              </Alert>
            )}
            <Alert status='warning' borderRadius='md'>
              <AlertIcon />
              <AlertDescription>
                Clicking "Create and send renewal" will send a Hellosign lease to the active
                tenants.
              </AlertDescription>
            </Alert>
            <Flex w='full' justify='flex-end' gap={3}>
              <Button
                minW='150px'
                variant='outline'
                isDisabled={isSending}
                onClick={() => {
                  setOpen(false)
                  goBack(true)
                }}
              >
                Edit renewal
              </Button>
              <Button
                colorScheme='teal'
                minW='230px'
                isLoading={isSending}
                isDisabled={Boolean(billingPreview?.postedCharges.length && !acknowledged)}
                onClick={handleSubmit}
              >
                Create and send renewal
              </Button>
            </Flex>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default RenewRentalConfirmation
